/**
 * HxStandardTabs Component
 *
 * A simple horizontal tabs component for switching between different content panels. Provides a clean, accessible tab
 * interface with optional icons.
 *
 * @example
 *   ```html
 *   <hx-standard-tabs
 *     [tabs]="tabsData"
 *     [activeTab]="activeTabIndex()"
 *     (tabClick)="onTabClick($event)">
 *   </hx-standard-tabs>
 *   ```;
 *
 * @package @complexcare/harmony
 */

import { ChangeDetectionStrategy, Component, input, output, ViewEncapsulation } from '@angular/core';
import { HxIcon } from '../icon/icon';
import type { HxStandardTab } from './types';

@Component({
  selector: 'hx-standard-tabs',
  standalone: true,
  imports: [HxIcon],
  templateUrl: './standard-tabs.html',
  styleUrls: ['./standard-tabs.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'hx-standard-tabs',
    '[attr.role]': '"tablist"',
  },
})
export class HxStandardTabs {
  // --- INPUTS ---

  /**
   * Array of tab configurations. Each tab should have an index and label, with optional icon.
   *
   * @required
   */
  readonly tabs = input.required<readonly HxStandardTab[]>();

  /**
   * Index of the currently active tab.
   *
   * @default 0
   */
  readonly activeTab = input<number>(0);

  /**
   * Whether to display tabs in a compact/sub-tab style (e.g., for nested tabs).
   *
   * @default false
   */
  readonly compact = input<boolean>(false);

  // --- OUTPUTS ---

  /** Emitted when a tab is clicked. Passes the tab index. */
  readonly tabClick = output<number>();

  // --- METHODS ---

  protected onTabClick(index: number): void {
    this.tabClick.emit(index);
  }

  protected trackByIndex(index: number): number {
    return index;
  }
}
